import {
  ArrowRight,
  ArrowDown,
  Activity,
  Users,
  Heart,
  MessageSquare,
  ShieldCheck,
  Check,
  ClipboardList,
  Award,
  FileText,
  Phone,
} from "lucide-react";
import SEO from "../components/SEO";
import { ALIGNMENT_CHECK_URL } from "../lib/site";

const STEPS = [
  { n: "01", title: "Take the assessment", body: "Complete a brief 5-minute assessment." },
  { n: "02", title: "Receive your score", body: "Receive your Alignment Score™." },
  { n: "03", title: "Review your report", body: "Review your personalized Alignment Report™." },
  { n: "04", title: "Identify opportunities", body: "Identify practical opportunities to improve consistency, accountability, and follow-through." },
];

const FRAMEWORK = [
  { label: "Step 1", title: "Alignment Check™", caption: "A 5-minute diagnostic" },
  { label: "Step 2", title: "Alignment Score™", caption: "A percentage-based view of operational alignment" },
  { label: "Step 3", title: "Alignment Report™", caption: "Personalized strengths and opportunities" },
  { label: "Step 4", title: "Alignment Review™", caption: "Optional consultation to discuss results" },
  { label: "Outcome", title: "Aligned Education Systems™", caption: "Where consistent follow-through becomes operational" },
];

const OUTCOME_AREAS = [
  { icon: Activity, title: "Student Success", body: "Evaluate how effectively attendance interventions are identified, managed, and monitored." },
  { icon: Users, title: "Student Support", body: "Assess consistency in behavior expectations, interventions, and communication." },
  { icon: Heart, title: "Family Engagement", body: "Measure the effectiveness of enrollment follow-up and family engagement processes." },
];

const SYSTEM_ENABLERS = [
  { icon: MessageSquare, title: "Communication Systems", body: "Assess the consistency and effectiveness of communication with families and stakeholders." },
  { icon: ShieldCheck, title: "Organizational Execution", body: "Evaluate accountability, ownership, and operational follow-through." },
];

const SAMPLE_QUESTIONS = [
  "Students at risk of chronic absenteeism are identified before attendance becomes a major concern.",
  "Staff members clearly understand who owns attendance intervention efforts.",
  "Follow-up responsibilities are clearly assigned and understood.",
  "Parent communication is proactive rather than reactive.",
  "Critical operational responsibilities have clearly assigned ownership.",
  "Leadership can easily monitor follow-through on key initiatives.",
  "We know where enrollment opportunities are being lost.",
];

const RECEIVE = [
  {
    icon: Award,
    title: "Alignment Score™",
    body: "A percentage-based score that measures operational alignment across key dimensions.",
  },
  {
    icon: FileText,
    title: "Alignment Report™",
    body: "A personalized report highlighting strengths, opportunities, and practical recommendations.",
  },
  {
    icon: Phone,
    title: "Alignment Review™",
    body: "An optional consultation to discuss your results and explore strategies used by high-performing schools.",
  },
];

const SAMPLE_INSIGHTS = [
  "Attendance interventions may be reactive rather than proactive.",
  "Enrollment follow-up may lack consistent ownership.",
  "Parent communication may vary across staff members.",
  "Important operational tasks may depend heavily on individual employees.",
  "Strong outcomes may be masking underlying execution challenges.",
];

const FAQ = [
  { q: "How long does the Alignment Check™ take?", a: "Approximately 5 minutes." },
  {
    q: "Who should complete the assessment?",
    a: "Principals, assistant principals, district leaders, enrollment teams, attendance teams, and other school administrators.",
  },
  {
    q: "What does the assessment evaluate?",
    a: "The Alignment Check™ evaluates five key dimensions: Student Success, Student Support, Family Engagement, Communication Systems, and Organizational Execution.",
  },
  {
    q: "Will I receive my results immediately?",
    a: "You will receive your Alignment Score™ immediately after completion. Your personalized Alignment Report™ will be delivered shortly afterward.",
  },
  { q: "Is there any cost?", a: "No. The Alignment Check™ is completely free." },
  {
    q: "What happens after I complete the assessment?",
    a: "You will receive your Alignment Score™, your personalized Alignment Report™, and the opportunity to schedule an Alignment Review™ if desired.",
  },
];

function StartButton({ testid, variant = "primary", label = "Start the Alignment Check\u2122" }) {
  const base =
    "inline-flex justify-center items-center gap-2 px-8 py-3.5 text-sm tracking-wide transition-colors border";
  const styles =
    variant === "primary"
      ? "bg-[#0A192F] text-[#FDFBF7] hover:bg-[#112240] border-[#0A192F]"
      : variant === "light"
      ? "bg-[#FDFBF7] text-[#0A192F] hover:bg-[#D4AF37] border-[#FDFBF7]"
      : "bg-transparent text-[#0A192F] hover:bg-[#F4F1EB] border-[#0A192F]";
  return (
    <a
      href={ALIGNMENT_CHECK_URL}
      target="_blank"
      rel="noreferrer"
      className={`${base} ${styles}`}
      data-testid={testid}
    >
      <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> {label}
    </a>
  );
}

export default function AlignmentCheck() {
  return (
    <>
      <SEO
        title="Alignment Check™ | Discover Your School's Alignment Score™ | Aligned Education Systems"
        description="A free 5-minute diagnostic for school leaders. Discover your Alignment Score™ and uncover opportunities to improve consistency, accountability, communication, and follow-through."
      />

      {/* 1. HERO */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="ac-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-28">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">
            A Leadership Diagnostic for K&ndash;12 Schools
          </div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] leading-[1.05] max-w-4xl">
            Take the Alignment Check&trade;
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            Discover your school&apos;s Alignment Score&trade; and uncover opportunities to improve consistency, accountability, communication, and follow-through.
          </p>
          <p className="mt-6 text-base text-[#1A1D20] leading-relaxed max-w-3xl">
            Most schools don&apos;t struggle because they lack dedicated people.
          </p>
          <p className="mt-2 font-serif-display text-xl sm:text-2xl text-[#52796F] leading-relaxed max-w-3xl">
            They struggle because important processes depend on individuals rather than systems.
          </p>
          <p className="mt-6 text-base text-[#4A5568] leading-relaxed max-w-3xl">
            The Alignment Check&trade; helps school leaders identify strengths, uncover opportunities, and evaluate the systems that drive student outcomes and organizational effectiveness.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <StartButton testid="ac-hero-start-btn" />
            <a
              href="#how-it-works"
              className="inline-flex justify-center items-center gap-2 bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]"
              data-testid="ac-hero-howitworks-btn"
            >
              How It Works <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
          <p className="mt-10 text-sm text-[#52796F] leading-relaxed max-w-2xl">
            Free &middot; Approximately 5 minutes &middot; Built for principals, district leaders, and school operations teams.
          </p>
        </div>
      </section>

      {/* 2. HOW IT WORKS */}
      <section id="how-it-works" className="py-24 bg-[#F4F1EB]" data-testid="ac-howitworks">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">How It Works</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Four Steps. About Five Minutes.
            </h2>
          </div>
          <ol className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <li
                key={s.n}
                className="relative bg-[#FDFBF7] border border-[#0A192F]/10 p-7 pt-9"
                data-testid={`ac-step-${i}`}
              >
                <span className="absolute -top-4 left-6 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FDFBF7] border-2 border-[#38BDF8] text-[#0A192F] font-serif-display text-base">
                  {s.n}
                </span>
                <div className="font-serif-display text-xl text-[#0A192F] leading-snug">{s.title}</div>
                <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 3. ALIGNMENT FRAMEWORK VISUAL */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="ac-framework">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">The Alignment Framework</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              From Diagnostic to Operational Alignment
            </h2>
            <p className="mt-6 text-base text-[#4A5568] leading-relaxed max-w-2xl mx-auto">
              A clear path from one assessment to a more consistent school operation.
            </p>
          </div>

          <div className="mt-16 flex flex-col items-center">
            {FRAMEWORK.map((node, i) => {
              const last = i === FRAMEWORK.length - 1;
              return (
                <div key={node.title} className="w-full max-w-2xl">
                  <div
                    className={`relative w-full text-center px-8 py-7 border ${
                      last
                        ? "bg-[#0A192F] text-[#FDFBF7] border-[#0A192F]"
                        : "bg-[#FDFBF7] text-[#0A192F] border-[#0A192F]/15"
                    }`}
                    data-testid={`ac-framework-node-${i}`}
                  >
                    <div
                      className={`text-xs uppercase tracking-widest ${
                        last ? "text-[#D4AF37]" : "text-[#52796F]"
                      }`}
                    >
                      {node.label}
                    </div>
                    <div className={`mt-2 font-serif-display text-2xl sm:text-3xl ${last ? "text-[#FDFBF7]" : "text-[#0A192F]"}`}>
                      {node.title}
                    </div>
                    <div className={`mt-2 text-sm ${last ? "text-[#FDFBF7]/80" : "text-[#4A5568]"} leading-relaxed`}>
                      {node.caption}
                    </div>
                  </div>
                  {!last && (
                    <div className="flex justify-center py-3" aria-hidden="true">
                      <ArrowDown className="w-6 h-6 text-[#38BDF8]" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. WHAT THE ALIGNMENT CHECK MEASURES */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="ac-dimensions">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">What It Measures</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Five Dimensions of Operational Alignment
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              The Alignment Check&trade; evaluates the systems that shape both student outcomes and the daily reality of running a school.
            </p>
          </div>

          {/* Outcome Areas */}
          <div className="mt-14">
            <div className="text-xs uppercase tracking-widest text-[#52796F]">Outcome Areas</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {OUTCOME_AREAS.map(({ icon: Icon, title, body }, i) => (
                <div key={title} className="bg-[#FDFBF7] border border-[#0A192F]/10 p-7" data-testid={`ac-outcome-${i}`}>
                  <Icon className="w-6 h-6 text-[#52796F]" strokeWidth={1.5} />
                  <div className="mt-4 font-serif-display text-xl text-[#0A192F]">{title}</div>
                  <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* System Enablers */}
          <div className="mt-12">
            <div className="text-xs uppercase tracking-widest text-[#52796F]">System Enablers</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {SYSTEM_ENABLERS.map(({ icon: Icon, title, body }, i) => (
                <div key={title} className="bg-[#FDFBF7] border border-[#0A192F]/10 p-7" data-testid={`ac-enabler-${i}`}>
                  <Icon className="w-6 h-6 text-[#52796F]" strokeWidth={1.5} />
                  <div className="mt-4 font-serif-display text-xl text-[#0A192F]">{title}</div>
                  <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5. SAMPLE ASSESSMENT QUESTIONS */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="ac-samples">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Inside the Assessment</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              A Look at What You&apos;ll Evaluate
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              A few of the statements school leaders rate during the Alignment Check&trade;.
            </p>
          </div>
          <ul className="mt-10 divide-y divide-[#0A192F]/10 border-t border-b border-[#0A192F]/10">
            {SAMPLE_QUESTIONS.map((q, i) => (
              <li key={q} className="py-5 flex items-start gap-5" data-testid={`ac-question-${i}`}>
                <span className="font-serif-display text-base text-[#38BDF8] mt-0.5 w-8 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-lg text-[#1A1D20] leading-relaxed">{q}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm italic text-[#52796F]">
            The full Alignment Check&trade; includes 25 questions across five operational dimensions.
          </p>
        </div>
      </section>

      {/* 6. WHY SCHOOL LEADERS TAKE IT */}
      <section className="py-24 bg-[#0A192F] text-[#FDFBF7]" data-testid="ac-why">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Why Leaders Take It</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            School leaders often know where they feel stretched.
          </h2>
          <p className="mt-8 text-lg text-[#FDFBF7]/85 leading-relaxed max-w-3xl">
            The Alignment Check&trade; helps identify where systems may be creating unnecessary workload, inconsistent follow-through, communication breakdowns, or missed opportunities.
          </p>
          <p className="mt-6 font-serif-display text-xl sm:text-2xl text-[#FDFBF7] leading-relaxed max-w-3xl">
            The result is a clearer picture of where attention and improvement efforts may have the greatest impact.
          </p>
        </div>
      </section>

      {/* 7. WHAT YOU WILL RECEIVE */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="ac-receive">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">What You Will Receive</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Three Deliverables. One Clear Direction.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {RECEIVE.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="bg-[#FDFBF7] border border-[#0A192F]/10 p-8" data-testid={`ac-receive-${i}`}>
                <Icon className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} />
                <div className="mt-5 font-serif-display text-2xl text-[#0A192F]">{title}</div>
                <p className="mt-4 text-[#4A5568] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. SAMPLE INSIGHTS */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="ac-insights">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Sample Insights</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              The Kind of Findings Leaders Uncover
            </h2>
          </div>
          <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 border-t border-[#0A192F]/10">
            {SAMPLE_INSIGHTS.map((insight, i) => (
              <li
                key={insight}
                className="flex items-start gap-3 py-5 border-b border-[#0A192F]/10"
                data-testid={`ac-insight-${i}`}
              >
                <Check className="w-5 h-5 text-[#D4AF37] mt-1.5 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-lg text-[#1A1D20] leading-relaxed">{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="ac-faq">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Frequently Asked</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            Common Questions
          </h2>
          <div className="mt-12 divide-y divide-[#0A192F]/10 border-t border-b border-[#0A192F]/10">
            {FAQ.map((item, i) => (
              <details key={item.q} className="group py-6" data-testid={`ac-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none gap-6">
                  <span className="font-serif-display text-lg sm:text-xl text-[#0A192F]">
                    {item.q}
                  </span>
                  <span className="text-[#52796F] text-xs uppercase tracking-widest group-open:hidden">
                    Open
                  </span>
                  <span className="text-[#52796F] text-xs uppercase tracking-widest hidden group-open:inline">
                    Close
                  </span>
                </summary>
                <p className="mt-4 text-base text-[#4A5568] leading-relaxed max-w-3xl">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="py-24 bg-[#0A192F] text-[#FDFBF7]" data-testid="ac-final-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Ready to Discover Your Alignment Score&trade;?
          </h2>
          <p className="mt-6 text-lg text-[#FDFBF7]/85 max-w-3xl mx-auto leading-relaxed">
            Complete the Alignment Check&trade; and receive a personalized report identifying strengths, opportunities, and areas for improvement.
          </p>
          <div className="mt-10 flex justify-center">
            <StartButton testid="ac-final-start-btn" variant="light" />
          </div>
          <p className="mt-6 text-sm text-[#FDFBF7]/65 leading-relaxed">
            Free &middot; About 5 minutes &middot; Results delivered immediately
          </p>
        </div>
      </section>
    </>
  );
}
